import { Modal } from '@/components/modal';
import { useCreateBrandAction } from '@/hooks/actions/use-create-brand-action';
import { Button, Field, HStack, Input, VStack } from '@chakra-ui/react';
import type { CreateBrandRequest } from '@fluenceur/types';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface CreateBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
}

export const CreateBrandModal = ({ isOpen, onClose }: CreateBrandModalProps) => {
  const { t } = useTranslation();
  const createBrandMutation = useCreateBrandAction();
  const isSubmitting = createBrandMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
    },
  });

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  }, [isSubmitting, reset, onClose]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: FormData) => {
    const payload: CreateBrandRequest = {
      name: data.name.trim(),
    };
    await createBrandMutation.mutateAsync(payload);
    handleClose();
  };

  const footer = (
    <HStack gap={3} justify="flex-end">
      <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
        {t('common.cancel')}
      </Button>
      <Button
        type="submit"
        form="create-brand-form"
        colorPalette="gray"
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        {t('brandMenu.createBrand')}
      </Button>
    </HStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('brandMenu.createBrandTitle')}
      description={t('brandMenu.createBrandDescription')}
      maxWidth="450px"
      isCentered
      closeOnInteractOutside={false}
      footer={footer}
    >
      <form id="create-brand-form" onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" gap={4}>
          <Field.Root invalid={!!errors.name}>
            <Field.Label>{t('brandMenu.brandName')}</Field.Label>
            <Input
              placeholder={t('brandMenu.brandNamePlaceholder')}
              {...register('name', {
                required: t('validation.required'),
                minLength: {
                  value: 1,
                  message: t('validation.required'),
                },
                maxLength: {
                  value: 255,
                  message: t('validation.maxLength', { max: 255 }),
                },
              })}
              autoFocus
            />
            {errors.name && <Field.ErrorText>{errors.name.message}</Field.ErrorText>}
          </Field.Root>
        </VStack>
      </form>
    </Modal>
  );
};
